import React, { useEffect, useMemo, useRef } from 'react';
import { Header } from 'new/elements/Form/Header';
import { Form } from 'new/elements/Form';
import { EnterCredentialPage } from 'new/components/flows/types/spark/CreateSparkFlow/EnterCredentialPage';
import { SelectConnectionPage } from 'new/components/flows/types/spark/CreateSparkFlow/SelectConnectionPage';
import { flowDataMap } from 'new/elements/flows/dataMap';
import { ROUTE_CONNECT } from 'new/constants/pathnames';
import { ROUTE_PIPELINES } from 'js/constants/pathnames';
import { FORM_TYPE_CREATE_SPARK } from 'js/constants/formTypes';
import { ResourceSetup } from 'new/components/flows/types/spark/CreateSparkFlow/ResourceSetup';
import useSpark from 'new/components/flows/types/spark/useSpark';
import flowTypes from 'new/components/flows/types/flowTypes';
import useFlowsAdapter from 'new/components/flows/adapter/useFlowsAdapter';
import useNexsetsAdapter from 'new/state/nexsets/useNexsetsAdapter';
import useFlowParams from 'new/components/flows/useFlowParams';
import FlowParamsProvider from 'new/components/flows/FlowParamsProvider';
import { HeaderMetaConfigProvider } from 'new/components/flows/context/HeaderMetaConfigProvider';

interface CreateSparkFlowProps {
  id?: number;
  flowType?: keyof typeof flowTypes;
  onDismiss?: (data: any) => void;
  isModal?: boolean;
  isEditMode?: boolean;
  resourceType?: 'source' | 'sink';
}

const CreateSparkFlow = (props: CreateSparkFlowProps) => {
  const { isModal, onDismiss } = props;
  const { resourceType, id, nexsetId } = useFlowParams();

  const start = useRef(true);
  const {
    data: formData,
    connectionType,
    updateFormData,
    resetFormData,
    startForm,
    source,
    nexset,
    endForm,
  } = useSpark();

  if (start.current) {
    startForm();
    updateFormData({
      connectionType: resourceType.toUpperCase(),
      flowType: flowTypes.spark,
    });
    start.current = false;
  }

  const { pollNexset } = useFlowsAdapter();
  const { requestNexset } = useNexsetsAdapter();

  const request = useMemo(
    () =>
      pollNexset(async (foundNexset) => {
        saveNexset(foundNexset.id);
      }),
    [],
  );

  const saveNexset = async (id) => {
    const nexset = await requestNexset(id);
    updateFormData({ nexset });
  };

  useEffect(() => {
    if (nexsetId) {
      saveNexset(nexsetId);
    }
  }, []);

  useEffect(() => {
    // Only request when creating a sink and we don't have nexset info
    if (source && connectionType === 'SINK' && !nexset && !nexsetId) {
      request(source?.origin_node_id);
    }
    return () => {
      request.cancel();
    };
  }, [source, nexset]);

  function resetFields() {
    resetFormData([
      'credentialId',
      'selectedCredentialInfo',
      'selectedConnection',
      'selectedBucket',
      'selectedTable',
      'table',
      'source',
      'sink',
      'nexset',
      'isNewTable',
      'sourceConfig',
      'sinkConfig',
    ]);
  }

  useEffect(() => {
    resetFields();
    return () => {
      resetFields();
      if (connectionType === 'SINK') {
        endForm();
      }
      request.cancel();
    };
  }, []);

  const onFormDismiss = () => {
    endForm();
    if (isModal) {
      onDismiss?.(formData);
      return true;
    }
    return false;
  };

  const data = flowDataMap.spark;
  const titleResourceType = resourceType === 'source' ? 'Source' : 'Destination';
  return (
    <HeaderMetaConfigProvider>
      <Form
        formType={FORM_TYPE_CREATE_SPARK}
        isModal={isModal}
        isEditMode={!!id}
        onDismiss={onFormDismiss}
        HeaderComponent={
          <Header
            hideContextualMenu={isModal}
            title={
              <>
                {data.shortTitle} <span style={{ color: 'var(--color-greyscale-200)' }}>/</span>{' '}
                {id ? 'Editing Data ' : 'New Data '}
                {titleResourceType}{' '}
              </>
            }
          />
        }
        description=""
        startPage={!!id ? 2 : undefined}
      >
        <SelectConnectionPage />
        <EnterCredentialPage />
        <ResourceSetup key={resourceType} />
      </Form>
    </HeaderMetaConfigProvider>
  );
};

const CreateSparkFlowContent = (props: CreateSparkFlowProps) => {
  return (
    <FlowParamsProvider {...props}>
      <CreateSparkFlow {...props} />
    </FlowParamsProvider>
  );
};

export default CreateSparkFlowContent;

CreateSparkFlowContent.screenConfig = {
  domain: 'main',
  path: [
    `${ROUTE_CONNECT}${ROUTE_PIPELINES}/spark/:resourceType/:id/edit`,
    `${ROUTE_CONNECT}${ROUTE_PIPELINES}/spark/:resourceType/new`,
  ],
  screenName: 'Spark Flow',
  pageTitle: 'Spark Flow',
  hideSideBar: true,
  header: {
    title: 'Spark Flow',
    slugTitle: 'Spark Flow',
  },
};
